import { getApiUrl, getDefaultHeaders } from '../lib/api';
import { 
  CAGConversation, 
  CAGResponse, 
  ConversationStats, 
  CreateConversationRequest, 
  CAGChatRequest 
} from '../types/cag';

export class CAGService {
  private static instance: CAGService;

  public static getInstance(): CAGService {
    if (!CAGService.instance) {
      CAGService.instance = new CAGService();
    }
    return CAGService.instance;
  }

  async createConversation(userId: string, gameId: string, token?: string): Promise<CAGConversation> {
    console.log('🚀 CAGService.createConversation called with:', { userId, gameId, hasToken: !!token });
    
    if (!userId) {
      console.error('❌ CAGService: userId is null/undefined:', userId);
      throw new Error('User ID is required');
    }
    
    if (!gameId) {
      console.error('❌ CAGService: gameId is null/undefined:', gameId);
      throw new Error('Game ID is required');
    }
    
    const request: CreateConversationRequest = {
      user_id: userId,
      game_id: gameId
    };

    console.log('🚀 CAG Request payload:', JSON.stringify(request, null, 2));
    console.log('🚀 CAG Request headers:', getDefaultHeaders(token));

    const requestBody = JSON.stringify(request);
    console.log('🚀 CAG Request body string:', requestBody);
    
    const response = await fetch(getApiUrl('/api/cag/conversations'), {
      method: 'POST',
      headers: getDefaultHeaders(token),
      body: requestBody,
    });

    console.log('🚀 CAG Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚨 CAG conversation creation failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        originalRequest: request,
        requestBody,
        headers: getDefaultHeaders(token),
        url: getApiUrl('/api/cag/conversations')
      });
      
      if (response.status === 429) {
        throw new Error('Maximum conversations per user exceeded. Please close some existing conversations.');
      }
      if (response.status === 404) {
        throw new Error('CAG_NOT_AVAILABLE');
      }
      throw new Error(`Failed to create conversation: ${errorText}`);
    }

    return await response.json();
  }

  async getConversation(sessionId: string, token?: string): Promise<CAGConversation> {
    const response = await fetch(getApiUrl(`/api/cag/conversations/${sessionId}`), {
      method: 'GET',
      headers: getDefaultHeaders(token),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Conversation not found or expired');
      }
      const errorText = await response.text();
      throw new Error(`Failed to get conversation: ${errorText}`);
    }

    return await response.json();
  }

  async sendMessage(sessionId: string, query: string, token?: string): Promise<CAGResponse> {
    const request: CAGChatRequest = {
      query
    };

    const response = await fetch(getApiUrl(`/api/cag/chat/${sessionId}`), {
      method: 'POST',
      headers: getDefaultHeaders(token),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Conversation not found or expired');
      }
      const errorText = await response.text();
      throw new Error(`Failed to send message: ${errorText}`);
    }

    return await response.json();
  }

  async getConversationStats(sessionId: string, token?: string): Promise<ConversationStats> {
    const response = await fetch(getApiUrl(`/api/cag/conversations/${sessionId}/stats`), {
      method: 'GET',
      headers: getDefaultHeaders(token),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Conversation not found');
      }
      const errorText = await response.text();
      throw new Error(`Failed to get conversation stats: ${errorText}`);
    }

    return await response.json();
  }

  // Helper method to check if a conversation is using CAG
  isCAGConversation(conversationId: string): boolean {
    return conversationId.startsWith('conv_');
  }

  // Helper method to format token usage for display
  formatTokenUsage(usage: { prompt: number; completion: number; total: number }): string {
    return `${usage.total} tokens (${usage.prompt} prompt + ${usage.completion} response)`;
  }

  // Helper method to format optimization stats
  formatOptimizationStats(stats: {
    rulesLoaded: number;
    budgetUsed: { coreRules: number; queryRules: number; history: number };
    historyLength: number;
  }): string {
    const total = stats.budgetUsed.coreRules + stats.budgetUsed.queryRules + stats.budgetUsed.history;
    return `${stats.rulesLoaded} rules loaded, ${total} tokens used (${stats.historyLength} messages in context)`;
  }
}